import jsPDF from "jspdf"
import autoTable from "jspdf-autotable"
import * as XLSX from "xlsx"
import { absoluteCampusLogoUrl } from "./campusLogos"

export type PrintTableBranding = {
  logoUrl?: string | null
  brandName?: string
  branchLabel?: string
}

/**
 * Opens a new window with a clean HTML table and triggers the browser print dialog.
 * Prints ALL rows passed — not limited to what is visible on screen.
 */
export const printTable = (
  title: string,
  headers: string[],
  rows: string[][],
  branding?: PrintTableBranding,
): void => {
  const win = window.open("", "_blank")
  if (!win) return

  const headCells = headers.map(h => `<th>${h}</th>`).join("")
  const bodyRows = rows
    .map(row => `<tr>${row.map(cell => `<td>${cell ?? ""}</td>`).join("")}</tr>`)
    .join("")

  const escapeHtml = (s: string) =>
    s.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;")

  // Resolve to an absolute URL — this HTML is written into a brand-new
  // about:blank window, so a relative path (e.g. the local static-file logo
  // fallback) would have no base URL to resolve against and silently fail.
  const brandHtml = branding?.brandName
    ? `<div class="brand">
        <img src="${escapeHtml(absoluteCampusLogoUrl(branding.logoUrl))}" onerror="this.style.display='none'" />
        <strong>${escapeHtml(branding.brandName)}${branding.branchLabel ? ` - ${escapeHtml(branding.branchLabel)}` : ""}</strong>
      </div>`
    : ""

  win.document.write(`<!DOCTYPE html>
<html>
  <head>
    <title>${title}</title>
    <style>
      body { font-family: Arial, sans-serif; font-size: 11px; margin: 20px; color: #111; }
      .brand { display: flex; align-items: center; gap: 8px; margin-bottom: 10px; }
      .brand img { height: 32px; object-fit: contain; }
      .brand strong { font-size: 14px; }
      h2 { font-size: 14px; margin-bottom: 12px; }
      table { width: 100%; border-collapse: collapse; }
      th, td { border: 1px solid #ccc; padding: 5px 8px; text-align: left; }
      th { background: #f0f0f0; font-weight: bold; }
      tr:nth-child(even) { background: #fafafa; }
      @media print { body { margin: 0; } }
    </style>
  </head>
  <body>
    ${brandHtml}
    <h2>${title}</h2>
    <table>
      <thead><tr>${headCells}</tr></thead>
      <tbody>${bodyRows}</tbody>
    </table>
  </body>
</html>`)
  win.document.close()
  win.focus()
  win.print()
  win.close()
}

/**
 * Generates a PDF using jsPDF + autoTable and triggers a download.
 * Default orientation is landscape for wide tables; use portrait for narrow ones.
 */
export const exportTableToPdf = (
  title: string,
  headers: string[],
  rows: string[][],
  filename: string,
  orientation: "portrait" | "landscape" = "landscape",
): void => {
  const doc = new jsPDF({ orientation, unit: "mm", format: "a4" })
  doc.setFontSize(11)
  doc.text(title, 14, 12)
  autoTable(doc, {
    head: [headers],
    body: rows,
    startY: 18,
    styles: { fontSize: 8, cellPadding: 2, overflow: "linebreak" },
    headStyles: { fillColor: [41, 128, 185], textColor: 255, fontStyle: "bold" },
    alternateRowStyles: { fillColor: [245, 245, 245] },
  })
  doc.save(filename)
}

/**
 * Generates an Excel file from an array of plain objects and triggers a download.
 * Keys of the first object become the column headers.
 */
export const exportTableToExcel = (
  rows: Record<string, any>[],
  sheetName: string,
  filename: string,
): void => {
  const ws = XLSX.utils.json_to_sheet(rows)
  const wb = XLSX.utils.book_new()
  XLSX.utils.book_append_sheet(wb, ws, sheetName)
  XLSX.writeFile(wb, filename)
}
